import React, { useContext, useEffect, useState } from 'react';
import { AppSettings } from './../../config/app-settings.js';

const themes = [
	['red', 'bg-red', 'Red'], ['pink', 'bg-pink', 'Pink'], ['orange', 'bg-orange', 'Orange'],
	['yellow', 'bg-yellow', 'Yellow'], ['lime', 'bg-lime', 'Lime'], ['green', 'bg-green', 'Green'],
	['teal', 'bg-teal', 'Default'], ['cyan', 'bg-cyan', 'Cyan'], ['blue', 'bg-blue', 'Blue'],
	['purple', 'bg-purple', 'Purple'], ['indigo', 'bg-indigo', 'Indigo'], ['gray-600', 'bg-black', 'Black']
];

const designVersions = [
	['https://seantheme.com/color-admin/admin/html/', 'default.jpg', true],
	['https://seantheme.com/color-admin/admin/transparent/', 'transparent.jpg'],
	['https://seantheme.com/color-admin/admin/apple/', 'apple.jpg'],
	['https://seantheme.com/color-admin/admin/material/', 'material.jpg'],
	['https://seantheme.com/color-admin/admin/facebook/', 'facebook.jpg'],
	['https://seantheme.com/color-admin/admin/google/', 'google.jpg']
];

const languageVersions = [
	['https://seantheme.com/color-admin/admin/html/', 'html.jpg'],
	['https://seantheme.com/color-admin/admin/ajax/', 'ajax.jpg'],
	['https://seantheme.com/color-admin/admin/angularjs/', 'angular1x.jpg'],
	['https://seantheme.com/color-admin/admin/angularjs22/', 'angular20x.jpg'],
	['https://seantheme.com/color-admin/admin/svelte/', 'svelte.jpg'],
	[null, 'laravel.jpg', false, 'Laravel Version only available in downloaded version.'],
	[null, 'django.jpg', false, 'Django Version only available in downloaded version.'],
	['https://seantheme.com/color-admin/admin/vue3/', 'vuejs.jpg'],
	['https://seantheme.com/color-admin/admin/react/', 'reactjs.jpg', true],
	[null, 'dotnet.jpg', false, '.NET Core MVC Version only available in downloaded version.'],
	['https://seantheme.com/color-admin/admin/nextjs/', 'nextjs.jpg']
];

const frontendVersions = [
	'https://seantheme.com/color-admin/frontend/one-page-parallax/',
	'https://seantheme.com/color-admin/frontend/e-commerce/',
	'https://seantheme.com/color-admin/frontend/blog/',
	'https://seantheme.com/color-admin/frontend/forum/',
	'https://seantheme.com/color-admin/frontend/corporate/'
];

function getStoredTheme() {
	const stored = localStorage.getItem('appTheme');
	return stored === 'dark' ? 'gray-600' : (stored || 'teal');
}

function VersionGrid({ items, folder }) {
	return (
		<div className="app-theme-version mb-4">
			{items.map((item, index) => {
				const [href, image, active, alertMessage] = Array.isArray(item) ? item : [item, imageName(item), false];
				const linkProps = href ? { href, target: folder === 'theme' ? '_self' : undefined } : { href: '#0', onClick: (event) => { event.preventDefault(); alert(alertMessage); } };
				return (
					<div className="app-theme-version-item" key={`${folder}-${image}-${index}`}>
						<a {...linkProps} className={`app-theme-version-link${active ? ' active' : ''}`}>
							<span style={{ backgroundImage: `url(/color-admin/admin/react/assets/img/${folder}/${image})` }} className="app-theme-version-cover"></span>
						</a>
					</div>
				);
			})}
		</div>
	);
}

function imageName(href) {
	return href.split('/').filter(Boolean).pop() + '.jpg';
}

function ThemePanel() {
	const context = useContext(AppSettings);
	const [expand, setExpand] = useState(() => localStorage.getItem('themePanelExpand') === 'true');
	const [theme, setTheme] = useState(getStoredTheme);
	const [rtlEnabled, setRtlEnabled] = useState(() => localStorage.getItem('rtlEnabled') === 'true');

	const handleToggle = (key, setter, handler) => (event) => {
		const value = event.target.checked;
		setter?.(value);
		handler(value);
		localStorage.setItem(key, value.toString());
	};

	const handleRtlToggle = (event) => {
		const value = event.target.checked;
		setRtlEnabled(value);
		document.documentElement.toggleAttribute('dir', value);
		if (value) document.documentElement.setAttribute('dir', 'rtl');
		localStorage.setItem('rtlEnabled', value.toString());
	};

	const toggleTheme = (event, value) => {
		event.preventDefault();
		setTheme(value);
		localStorage.setItem('appTheme', value);
		context.handleSetAppTheme(value);
	};

	useEffect(() => {
		context.handleSetAppTheme(getStoredTheme());
		context.handleSetAppDarkMode(localStorage.getItem('appDarkMode') === 'true');
		if (localStorage.getItem('rtlEnabled') === 'true') document.documentElement.setAttribute('dir', 'rtl');
		[
			['appHeaderFixed', context.handleSetAppHeaderFixed],
			['appHeaderInverse', context.handleSetAppHeaderInverse],
			['appSidebarFixed', context.handleSetAppSidebarFixed],
			['appGradientEnabled', context.handleSetAppGradientEnabled]
		].forEach(([key, handler]) => {
			const value = localStorage.getItem(key);
			if (value !== null) handler(value === 'true');
		});
		// Theme settings are intentionally restored once when the panel mounts.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={`app-theme-panel${expand ? ' active' : ''}`}>
			<a href="#0" onClick={(event) => { event.preventDefault(); const value = !expand; setExpand(value); localStorage.setItem('themePanelExpand', value.toString()); }} className="app-theme-collapse-btn" aria-label="Open app settings"><i className="fa fa-cog"></i></a>
			<div className="app-theme-panel-content" data-scrollbar="true" data-height="100%">
				<div className="fw-bold mb-2 fs-16px">App Settings</div>
				<div className="app-theme-list mb-4">
					{themes.map(([value, color, title]) => <div className={`app-theme-list-item${theme === value ? ' active' : ''}`} key={value}><a href="#0" onClick={(event) => toggleTheme(event, value)} className={`app-theme-list-link ${color}`} data-bs-toggle="tooltip" data-bs-title={title}>&nbsp;</a></div>)}
				</div>

				<div className="bg-light rounded px-3 py-3 mb-4">
					<div className="row">
						<div className="col-9 control-label text-body fw-bold"><div className="d-flex mb-2px">Dark Mode <span className="badge bg-primary ms-1 position-relative d-flex align-items-center">NEW</span></div><div className="lh-14 fs-11px fw-semibold text-body opacity-50">Adjust the appearance to reduce glare and give your eyes a break.</div></div>
						<div className="col-3 d-flex"><div className="form-check form-switch d-flex justify-content-end mb-0 ms-auto"><input type="checkbox" className="form-check-input" id="appThemeDarkMode" checked={context.appDarkMode} onChange={handleToggle('appDarkMode', null, context.handleSetAppDarkMode)} /><label className="form-check-label" htmlFor="appThemeDarkMode"></label></div></div>
					</div>
					<hr className="mx-n3" />
					{[
						['Header Fixed', 'appHeaderFixed', context.appHeaderFixed, context.handleSetAppHeaderFixed],
						['Header Inverse', 'appHeaderInverse', context.appHeaderInverse, context.handleSetAppHeaderInverse],
						['Sidebar Fixed', 'appSidebarFixed', context.appSidebarFixed, context.handleSetAppSidebarFixed],
						['Gradient Enabled', 'appGradientEnabled', context.appGradientEnabled, context.handleSetAppGradientEnabled]
					].map(([label, key, checked, handler]) => <div className="row mb-2 align-items-center" key={key}><div className="col-9 fs-13px text-body fw-bold">{label}</div><div className="col-3 d-flex"><div className="form-check form-switch d-flex justify-content-end mb-0 ms-auto"><input type="checkbox" className="form-check-input" id={key} checked={checked} onChange={handleToggle(key, null, handler)} /><label className="form-check-label" htmlFor={key}></label></div></div></div>)}
					<div className="row align-items-center"><div className="col-9 fs-13px d-flex text-body fw-bold">RTL Enabled <span className="badge bg-primary ms-1 position-relative d-flex align-items-center">NEW</span></div><div className="col-3 d-flex"><div className="form-check form-switch d-flex justify-content-end mb-0 ms-auto"><input type="checkbox" className="form-check-input" id="appRtlMode" checked={rtlEnabled} onChange={handleRtlToggle} /><label className="form-check-label" htmlFor="appRtlMode"></label></div></div></div>
				</div>

				<div className="app-theme-panel-divider"></div>
				<div className="fw-bold mb-2 fs-16px">Admin Design (6)</div>
				<VersionGrid items={designVersions} folder="theme" />
				<div className="app-theme-panel-divider"></div>
				<div className="fw-bold mb-2 fs-16px">Language Version (9)</div>
				<VersionGrid items={languageVersions} folder="version" />
				<div className="app-theme-panel-divider"></div>
				<div className="fw-bold mb-2 fs-16px">Frontend Design (5)</div>
				<VersionGrid items={frontendVersions.map((href) => [href, imageName(href)])} folder="theme" />
				<div className="app-theme-panel-divider"></div>
				<a href="https://seantheme.com/color-admin/documentation/" className="btn btn-dark d-flex align-items-center justify-content-center w-100 rounded-3 mb-2 h-40px" target="_blank" rel="noreferrer">Documentation</a>
				<a href="#0" className="btn btn-default d-flex align-items-center justify-content-center w-100 rounded-3 h-40px" onClick={(event) => { event.preventDefault(); localStorage.clear(); window.location.reload(); }}>Reset Storage &amp; Cookies</a>
			</div>
		</div>
	);
}

export default ThemePanel;
